import React, { useState, useRef, useEffect } from 'react';
import { PLATFORMS, SocialPlatform, generateSharingUrl, copyToClipboard, ShareableContent } from '../../utils/sharingUrls';
import './ShareButton.css';

interface ShareButtonProps {
  content: ShareableContent;
  variant?: 'icon' | 'text' | 'both';
  size?: 'small' | 'medium' | 'large';
  platforms?: SocialPlatform[];
  showCopyButton?: boolean;
  className?: string;
  onShare?: (platform: SocialPlatform) => void;
}

const ShareButton: React.FC<ShareButtonProps> = ({ 
  content, 
  variant = 'both', 
  size = 'medium',
  platforms = ['whatsapp', 'twitter', 'facebook', 'pinterest', 'linkedin'],
  showCopyButton = true,
  className = '',
  onShare
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const touchHandledRef = useRef(false);
  
  // Check if Web Share API is available
  const isWebShareAvailable = typeof navigator !== 'undefined' && 'share' in navigator;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside as EventListener);
      document.addEventListener('touchstart', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, [isOpen]);

  // Handle Web Share API
  const handleWebShare = async () => {
    if (!isWebShareAvailable) return;
    
    try {
      const { title, description, url, hashtags } = content;
      const hashtagString = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';
      // Include full description with URL at the end for better formatting
      const text = `${title}\n\n${description}${hashtagString ? '\n\n' + hashtagString : ''}\n\n🔗 ${url}`;
      
      // Build share data - files property is only supported in some browsers
      const shareData: ShareData = {
        title,
        text,
        url
      };
      
      // Try to include image as file if supported (Web Share API Level 2)
      // Note: files property is not widely supported yet, so we include it conditionally
      if (content.image && 'canShare' in navigator) {
        try {
          const testShareData: any = { files: [] };
          // Check if files are supported by testing with empty array
          if (navigator.canShare && navigator.canShare(testShareData)) {
            try {
              const response = await fetch(content.image);
              const blob = await response.blob();
              const file = new File([blob], 'share-image.jpg', { type: blob.type });
              (shareData as any).files = [file];
            } catch (e) {
              // If image fetch fails, continue without image
              console.warn('Could not fetch image for Web Share API:', e);
            }
          }
        } catch (e) {
          // Files not supported, continue without files
        }
      }
      
      await navigator.share(shareData);
      
      if (onShare) {
        onShare('instagram' as SocialPlatform); // Track as Instagram since Web Share is used for Instagram
      }
      
      setIsOpen(false);
    } catch (error: any) {
      // User cancelled or error occurred
      if (error.name !== 'AbortError') {
        console.error('Web Share API error:', error);
      }
    }
  };

  const handleShare = (platform: SocialPlatform) => {
    // Handle Instagram - use Web Share API if available, otherwise copy link
    if (platform === 'instagram') {
      if (isWebShareAvailable) {
        handleWebShare();
        return;
      } else {
        // Instagram doesn't support direct URL sharing, so copy the link
        const { title, description, url, hashtags } = content;
        const hashtagString = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';
        const textToCopy = `${title}\n\n${description}\n\n${url}${hashtagString ? '\n\n' + hashtagString : ''}`;
        copyToClipboard(textToCopy).then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        });
        setIsOpen(false);
        return;
      }
    }
    
    const url = generateSharingUrl(platform, content);

    if (platform === 'whatsapp') {
      const { title, description, hashtags } = content;
      const hashtagString = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';
      const message = `${content.url}\n\n${title}\n\n${description}${hashtagString ? '\n\n' + hashtagString : ''}`.trim();
      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
    } else {
      // Open sharing URL in new window for other platforms
      const windowFeatures = 'width=600,height=400,scrollbars=yes,resizable=yes';
      window.open(url, '_blank', windowFeatures);
    }
    
    // Track sharing event
    if (onShare) {
      onShare(platform);
    }
    
    // Close dropdown
    setIsOpen(false);
  };

  const handleCopyLink = async () => {
    // Get admin share template
    const shareTemplate = localStorage.getItem('share_message_template') || '';
    
    // Build message with template if available
    let textToCopy = content.url;
    if (shareTemplate) {
      const { title, description, hashtags, category } = content;
      const hashtagString = hashtags ? hashtags.map(tag => `#${tag}`).join(' ') : '';
      
      const buildMessageFromTemplate = (tmpl: string) => {
        return tmpl
          .replace(/\{title\}/gi, title)
          .replace(/\{category\}/gi, category || '')
          .replace(/\{url\}/gi, content.url)
          .replace(/\{description\}/gi, description)
          .replace(/\{hashtags\}/gi, hashtagString);
      };
      
      const customMessage = buildMessageFromTemplate(shareTemplate);
      textToCopy = `${customMessage}\n\n${content.url}`;
    }
    
    const success = await copyToClipboard(textToCopy);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const getPlatformConfig = (platformId: SocialPlatform) => {
    return PLATFORMS.find(p => p.id === platformId);
  };

  const availablePlatforms = platforms.map(platformId => ({
    ...getPlatformConfig(platformId)!
  }));

  const handleButtonInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    // On mobile, if touch was already handled, ignore the click
    if (e.type === 'click' && touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    
    if (e.type === 'touchstart') {
      touchHandledRef.current = true;
      setTimeout(() => {
        touchHandledRef.current = false;
      }, 300);
    }
    
    setIsOpen(prev => !prev);
  };

  return (
    <div className={`share-button-container ${className}`} ref={dropdownRef}>
      <button 
        ref={buttonRef}
        className={`share-button ${size} ${variant}`}
        onClick={handleButtonInteraction}
        onTouchStart={handleButtonInteraction}
        aria-label="Share content"
        type="button"
      >
        <i className="fas fa-share-alt"></i>
        {variant !== 'icon' && <span>Share</span>}
      </button>
      
      {isOpen && (
        <>
          {/* Mobile backdrop overlay */}
          <div 
            className="share-dropdown-backdrop"
            onClick={() => setIsOpen(false)}
            onTouchStart={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div className="share-dropdown">
            <div className="share-dropdown-header">
              <h4>Share this content</h4>
              <button 
                className="close-dropdown"
                onClick={() => setIsOpen(false)}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                aria-label="Close sharing options"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          
          <div className="share-platforms">
            {/* Web Share API button (shown first when available) */}
            {isWebShareAvailable && (
              <button
                className="share-platform-btn web-share-btn"
                onClick={handleWebShare}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  handleWebShare();
                }}
                style={{ '--platform-color': '#8B4513' } as React.CSSProperties}
                aria-label="Share using device options"
                title="Share using your device's native share options (includes Instagram, Messages, etc.)"
              >
                <i className="fas fa-share-alt"></i>
                <span>Share</span>
              </button>
            )}
            
            {availablePlatforms.map(platform => {
              return (
                <button
                  key={platform.id}
                  className="share-platform-btn"
                  onClick={() => handleShare(platform.id)}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleShare(platform.id);
                  }}
                  style={{ '--platform-color': platform.color } as React.CSSProperties}
                  aria-label={`Share on ${platform.name}`}
                  title={platform.id === 'instagram' && !isWebShareAvailable ? 'Copy link to share on Instagram' : undefined}
                >
                  <i className={platform.icon}></i>
                  <span>{platform.name}</span>
                </button>
              );
            })}
          </div>
          
          {showCopyButton && (
            <div className="share-actions">
              <button 
                className={`copy-link-btn ${copySuccess ? 'success' : ''}`}
                onClick={handleCopyLink}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  handleCopyLink();
                }}
                aria-label="Copy link to clipboard"
              >
                <i className={copySuccess ? 'fas fa-check' : 'fas fa-copy'}></i>
                <span>{copySuccess ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          )}
          
          <div className="share-preview">
            <div className="preview-content">
              <div className="preview-title">{content.title}</div>
              <div className="preview-description">{content.description}</div>
              <div className="preview-url">{content.url}</div>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default ShareButton;

